import { useState, useMemo } from 'react';
import { Users, ChevronDown, ChevronUp } from 'lucide-react';
import { EmployeeAvatar } from '../../../components/Shared/EmployeeAvatar';
import { type EmployeePlanning } from '../../../types/planning';

interface EmployeeSelectorProps {
  employees: EmployeePlanning[];
  selectedEmployeeIds: string[];
  onToggleEmployee: (employeeId: string) => void;
  /** Select or deselect all employees in a group (by ids) */
  onToggleGroup?: (employeeIds: string[], select: boolean) => void;
}

const ROLE_ORDER: string[] = ['Mitarbeiter', 'Verwaltung', 'SuperAdmin'];
const ROLE_LABELS: Record<string, string> = {
  Mitarbeiter: 'Mitarbeiter',
  Verwaltung: 'Verwaltung',
  SuperAdmin: 'SuperAdmin',
};

function getRoleSortKey(role: string | undefined): number {
  const idx = ROLE_ORDER.indexOf(role || '');
  return idx >= 0 ? idx : ROLE_ORDER.length;
}

function chipTone(isSelected: boolean): string {
  if (isSelected) {
    return 'bg-emerald-50 border-[#00A871]/40 text-slate-900';
  }
  return 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50';
}

export function EmployeeSelector({
  employees,
  selectedEmployeeIds,
  onToggleEmployee,
  onToggleGroup,
}: EmployeeSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const groups = useMemo(() => {
    const byRole = new Map<string, EmployeePlanning[]>();
    for (const emp of employees) {
      const role = emp.role || 'Mitarbeiter';
      if (!byRole.has(role)) byRole.set(role, []);
      byRole.get(role)!.push(emp);
    }
    const roles = Array.from(byRole.keys()).sort((a, b) => {
      const keyA = getRoleSortKey(a);
      const keyB = getRoleSortKey(b);
      if (keyA !== keyB) return keyA - keyB;
      return a.localeCompare(b);
    });
    return roles.map((role) => ({
      role,
      label: ROLE_LABELS[role] || role,
      employees: byRole.get(role)!,
    }));
  }, [employees]);

  const allIds = useMemo(() => employees.map((e) => e.id), [employees]);
  const allSelected = selectedEmployeeIds.length === employees.length && employees.length > 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" strokeWidth={2} />
          <span className="text-[13px] font-medium text-slate-900">Mitarbeiter</span>
          <span className="text-[12px] text-slate-500 tabular-nums">
            ({selectedEmployeeIds.length}/{employees.length})
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onToggleGroup && allIds.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onToggleGroup(allIds, !allSelected);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onToggleGroup(allIds, !allSelected);
                }
              }}
              className="text-[11px] font-medium text-[#00A871] hover:underline cursor-pointer"
            >
              {allSelected ? 'Keine' : 'Alle'}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" strokeWidth={2} />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={2} />
          )}
        </div>
      </button>

      {/* Collapsed: avatar stack of selected */}
      {!isExpanded && selectedEmployeeIds.length > 0 && (
        <div className="px-3 sm:px-4 pb-2.5 pt-0 -mt-1 flex items-center gap-1 flex-wrap">
          {employees
            .filter((e) => selectedEmployeeIds.includes(e.id))
            .slice(0, 12)
            .map((emp) => (
              <span
                key={emp.id}
                title={emp.name}
                className="inline-block"
              >
                <EmployeeAvatar
                  employee={{ id: emp.id, name: emp.name, avatarColor: emp.avatarColor }}
                  size={20}
                />
              </span>
            ))}
          {selectedEmployeeIds.length > 12 && (
            <span className="text-[11px] text-slate-500 tabular-nums ml-1">
              +{selectedEmployeeIds.length - 12}
            </span>
          )}
        </div>
      )}

      {/* Expanded list */}
      {isExpanded && (
        <div className="px-3 sm:px-4 pb-3 space-y-3 border-t border-slate-100 pt-3">
          {groups.map(({ role, label, employees: groupEmployees }) => {
            const ids = groupEmployees.map((e) => e.id);
            const groupSelected = ids.every((id) => selectedEmployeeIds.includes(id));
            return (
              <div key={role}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                    {label}
                  </span>
                  {onToggleGroup && ids.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onToggleGroup(ids, !groupSelected)}
                      className="text-[11px] font-medium text-[#00A871] hover:underline"
                    >
                      {groupSelected ? 'Keine' : 'Alle'}
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {groupEmployees.map((employee) => {
                    const isSelected = selectedEmployeeIds.includes(employee.id);
                    return (
                      <button
                        key={employee.id}
                        type="button"
                        onClick={() => onToggleEmployee(employee.id)}
                        aria-pressed={isSelected}
                        className={`inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border text-[12px] transition-colors ${chipTone(isSelected)}`}
                      >
                        <span className={isSelected ? '' : 'opacity-70'}>
                          <EmployeeAvatar
                            employee={{
                              id: employee.id,
                              name: employee.name,
                              avatarColor: employee.avatarColor,
                            }}
                            size={18}
                          />
                        </span>
                        <span className="font-medium">{employee.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
